import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css"; // Import styles
import { FaArrowRight, FaCamera, FaSpinner, FaEye, FaEyeSlash } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import Navbar from "../../components/Navbar";
import imageCompression from 'browser-image-compression';
import countries from '../../data/countries.json';

import {
  saveUserDetails,
  setError,
  getUserProfile,
  setLoading,
} from "../../Redux/userSlice";
import ContentType from "./ContentType";
import { updateUserDetails } from '../services/influancerService';
import { formatDate } from "../../utils/dateUtils";

const UserDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [imageLoading, setImageLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');

  // Get redirectTo parameter from URL
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get('redirectTo');


  const { fetchingUserDetails, loading, error } = useSelector(
    (state) => state.user
  );
  const user = useSelector((state) => state.user.data);

  const [formData, setFormData] = React.useState({
    email: "",
    mobile: "",
    gender: "",
    dob: "",
    country: "",
    state: "",
    city: "",
    postalCode: "",
    contentType: [],
    profileImage: null,
  });

  // Fetch user data on component mount
  useEffect(() => {
    dispatch(getUserProfile());
  }, [dispatch]);

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || "",
        mobile: user.mobile || "",
        gender: user.gender || "",
        dob: user.dob ? formatDate(user.dob) : "",
        country: user.country || "",
        state: user.state || "",
        city: user.city || "",
        postalCode: user.postalCode || "",
        contentType: user.contentType || [],
        profileImage: user.profileImage?.url || null
      });
    }
  }, [user]);

  if (loading || fetchingUserDetails) {
    return (
      <>
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-screen bg-orange-500 relative">
          <div className="animate-spin mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.354a8.001 8.001 0 11-4.244 14.907M4.354 12a8.001 8.001 0 0114.907-4.244"
              />
            </svg>
          </div>
          <div className="text-center text-white">
            <h1 className="text-sm md:text-lg lg:text-xl font-semibold">Update Your Details</h1>
            <p className="text-sm md:text-lg lg:text-sm mt-2">This shouldn't take long</p>
          </div>
        </div>
      </>
    );
  }

  const validatePassword = (pass) => {
    if (!pass) return true; // Allow empty password
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&^'"]{8,}$/;
    return passwordRegex.test(pass);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (newPassword && !validatePassword(newPassword)) {
      setPasswordError('Password must be at least 8 characters long and contain at least one letter and one number');
    } else {
      setPasswordError('');
    }
  };
  const isValidIndianName = (value) => {
    if (!value) return true; // allow empty if optional

    const trimmedValue = value.trim();

    const regex = /^[A-Za-z]+([ .-][A-Za-z]+)*$/;

    return regex.test(trimmedValue);
  };

  const validateForm = () => {
    if (!formData.email.includes("@")) {
      dispatch(setError("Invalid email address."));
      return false;
    }
    if (formData.mobile.replace(/\D/g, "").length < 10) {
      dispatch(setError("Mobile number must be at least 10 digits."));
      return false;
    }
    if (formData.postalCode && !/^[0-9]{6}$/.test(formData.postalCode)) {
      dispatch(setError("Postal code must be 6 digits."));
      return false;
    }
    if (formData.state && !isValidIndianName(formData.state)) {
      dispatch(setError("State name is invalid. Use letters, spaces, dots or hyphens only."));
      return false;
    }

    if (formData.city && !isValidIndianName(formData.city)) {
      dispatch(setError("City name is invalid. Use letters, spaces, dots or hyphens only."));
      return false;
    }

    // Validate age (must be at least 5 years old)
    if (formData.dob) {
      const today = new Date();
      const birthDate = new Date(formData.dob);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      // Check if birthday has occurred this year
      const hasBirthdayOccurred = monthDiff > 0 || (monthDiff === 0 && today.getDate() >= birthDate.getDate());
      const actualAge = hasBirthdayOccurred ? age : age - 1;

      if (actualAge < 5) {
        dispatch(setError("You must be at least 5 years old to register."));
        return false;
      }
    }

    if (password && !validatePassword(password)) {
      dispatch(setError("Password must be at least 8 characters long and contain at least one letter and one number"));
      return false;
    }

    return true;
  };

  // Calculate max date (5 years ago from today)
  const getMaxDate = () => {
    const today = new Date();
    const maxDate = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate());
    return maxDate.toISOString().split('T')[0];
  };

  // Calculate min date (100 years ago from today)
  const getMinDate = () => {
    const today = new Date();
    const minDate = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate());
    return minDate.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const changes = {
        email: formData.email.toLowerCase().trim(),
        mobile: formData.mobile,
        contentType: formData.contentType,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        postalCode: formData.postalCode
      };

      // Only include password if it's being changed
      if (password && password.trim()) {
        if (!validatePassword(password)) {
          dispatch(setError('Password must be at least 8 characters long and contain at least one letter and one number'));
          return;
        }

        // If user already has a password, require current password
        if (user?.hasPassword) {
          if (!currentPassword || !currentPassword.trim()) {
            dispatch(setError('Please enter your current password to set a new one.'));
            return;
          }
          changes.currentPassword = currentPassword.trim();
        }
        changes.password = password.trim();
      }

      // Check optional fields
      if (formData.gender !== user.gender) {
        changes.gender = formData.gender;
      }
      if (formData.dob !== formatDate(user.dob)) {
        changes.dob = formData.dob;
      }

      dispatch(setLoading(true));
      const response = await updateUserDetails(changes);

      if (response.success && response.user) {
        dispatch(saveUserDetails(response.user));
        // Clear password fields after successful update
        setPassword('');
        setCurrentPassword('');
        setPasswordError('');

        // Redirect based on profile completion and redirectTo parameter
        if (response.user.isProfileComplete) {
          if (redirectTo) {
            navigate(redirectTo);
          } else {
            navigate("/influencer/dashboard");
          }
        } else {
          navigate("/influencer/user-details");
        }
      } else {
        dispatch(setError(response.error || "Failed to update profile"));
      }
    } catch (err) {
      console.error('Error updating user details:', err);
      dispatch(setError(err.message || "Failed to update profile"));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleContentChange = (contents) => {
    setFormData((prev) => ({
      ...prev,
      contentType: contents,
    }));
  };
  const handlePhoneChange = (value) => {
    setFormData({
      ...formData,
      mobile: value, // Storing full number with country code
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      dispatch(setError('Please upload an image file'));
      return;
    }

    try {
      setImageLoading(true);

      // Compress the image with optimized settings
      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        initialQuality: 0.8
      };

      const compressedFile = await imageCompression(file, options);

      // Convert compressed file to base64
      const reader = new FileReader();
      reader.readAsDataURL(compressedFile);
      reader.onloadend = async () => {
        try {
          const response = await updateUserDetails({ profileImage: reader.result });

          if (response.success) {
            // Update form data with the new image URL
            setFormData(prev => ({
              ...prev,
              profileImage: response.profileImage.url
            }));

            // Refresh user profile data
            dispatch(getUserProfile());
          } else {
            dispatch(setError(response.message || 'Failed to update profile image'));
          }
        } catch (error) {
          console.error('Error uploading image:', error);
          dispatch(setError('Error uploading image. Please try again.'));
        } finally {
          setImageLoading(false);
        }
      };
    } catch (error) {
      console.error('Error processing image:', error);
      setImageLoading(false);
      dispatch(setError('Error processing image. Please try again.'));
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">

      <Navbar />

      <div className="w-full mt-2 text-left  md:text-center">
        <p className="text-sm md:text-lg lg:text-lg ml-4 font-bold text-gray-900">
          Hello{" "}
          <strong className="text-sm md:text-lg lg:text-lg font-bold text-gray-700">
            {user?.username || "User"}
          </strong>
        </p>
        <p className="text-sm md:text-lg lg:text-gray-600 ml-4 text-sm mt-1 tracking-tight lg:tracking-wider">
          We need a few more details to complete your profile.
        </p>
      </div>

      {/* Profile Image Upload */}
      <div className="mt-6 flex justify-center">
        <div className="relative">
          <div className="h-32 w-32 rounded-full overflow-hidden border-4 border-orange-500">
            {formData.profileImage ? (
              <img
                src={formData.profileImage}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-orange-100 flex items-center justify-center text-4xl font-bold text-orange-500">
                {user?.username?.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <label
            htmlFor="profile-image"
            className="absolute bottom-0 right-0 bg-orange-500 text-white p-2 rounded-full cursor-pointer hover:bg-orange-600 transition-colors"
          >
            <FaCamera />
            <input
              type="file"
              id="profile-image"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              disabled={imageLoading}
            />
          </label>
          {imageLoading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
              <FaSpinner className="animate-spin text-white text-2xl" />
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className=" mt-2 mx-auto w-full max-w-xl ">
        <div className="bg-white  rounded-lg p-4 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded relative text-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <form className="space-y-6 grid" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <input
                name="email"
                type="email"
                required
                className="appearance-none block w-full px-4 py-2 md:py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
              />
              <span className="text-gray-500 text-sm tracking-tight lg:tracking-wider">
                Your earning statement will be shared on this email
              </span>

              {/* Password Section */}

              {user?.hasPassword && (
                <div className="relative">
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="appearance-none block w-full px-4 py-2 md:py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                    placeholder="Enter current password"
                  />
                </div>
              )}

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  className="appearance-none block w-full px-4 py-2 md:py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                  placeholder="Set password for email login"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <FaEyeSlash className="h-5 w-5 text-gray-400" />
                  ) : (
                    <FaEye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>

              <span className="text-gray-500 text-sm tracking-tight lg:tracking-wider">
                Use this password for login with email and password in future.
              </span>

              {passwordError && (
                <p className="text-red-500 text-sm mt-1">{passwordError}</p>
              )}


              {/* Phone Input with Country Code */}

              <PhoneInput
                country={"in"} // Default to India (+91)
                value={formData.mobile}
                onChange={handlePhoneChange}
                inputProps={{
                  name: "mobile",
                  required: true,
                  className:
                    "block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400 sm:text-sm",
                }}
              />


              <div className="relative flex items-center">
                <select
                  name="gender"
                  required
                  className="appearance-none block w-full px-4 py-2 md:py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
                <IoIosArrowDown size={18} className="absolute right-5 text-gray-700 pointer-events-none" />
              </div>

              <input
                name="dob"
                type="date"
                required
                max={getMaxDate()}
                min={getMinDate()}
                className="appearance-none block w-full px-4 py-2 md:py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                value={formData.dob}
                onChange={handleChange}
              />
              <span className="text-gray-500 text-sm tracking-tight lg:tracking-wider">
                You must be at least 5 years old to register
              </span>

              {/* Country Selection */}
              <div className="relative flex items-center">
                <select
                  name="country"
                  required
                  className="appearance-none block w-full px-4 py-2 md:py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                  value={formData.country}
                  onChange={handleChange}
                >
                  <option value="">Select Country</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
                <IoIosArrowDown size={18} className="absolute right-5 text-gray-700 pointer-events-none" />
              </div>

              {/* State Input */}
              <input
                name="state"
                type="text"
                required
                className="appearance-none block w-full px-4 py-2 md:py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                placeholder="State or Province"
                value={formData.state}
                onChange={handleChange}
              />

              {/* City Input */}

              <input
                name="city"
                type="text"
                required
                className="appearance-none block w-full px-4 py-2 md:py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                placeholder="City"
                value={formData.city}
                onChange={handleChange}
              />

              {/* Postal Code Input */}
              <input
                name="postalCode"
                type="text"
                required
                maxLength="6"
                pattern="[0-9]{6}"
                className="appearance-none block w-full px-4 py-2 md:py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                placeholder="Postal Code (6 digits)"
                value={formData.postalCode}
                onChange={handleChange}
              />

              <div className="flex flex-col gap-0">
                <ContentType
                  onContentChange={handleContentChange}
                  initialContentTypes={formData.contentType}
                />
              </div>

              <div className="">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2 md:py-3 px-4 border border-transparent text-md lg:text-lg font-semibold tracking-wider rounded-md bg-orange-500 hover:bg-rose-400 text-white transition-colors duration-200 ease-in-out flex items-center justify-center gap-1"
                >
                  {loading ? "Submitting..." : "Continue"}
                  <FaArrowRight className="text-white text-sm" />
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
export default UserDetails;
